// src/components/SEOHead.jsx
// Dynamic SEO meta tags and structured data component

import { Helmet } from 'react-helmet-async'

export default function SEOHead({ 
  title, 
  description, 
  canonicalUrl, 
  type = 'website',
  image = '/newlogo.png',
  schemaData = null 
}) {
  const siteTitle = 'Netwoke - Network Monitoring for Mac'
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle
  const siteDescription = description || 'Monitor your Mac\'s network connections in real-time. Spot suspicious activity, see which apps are connecting where, and protect your privacy with Netwoke.'
  const siteUrl = 'https://netwoke.app'
  const fullUrl = canonicalUrl || siteUrl

  // Default structured data for the main site
  const defaultSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Netwoke",
    "description": siteDescription,
    "url": siteUrl,
    "applicationCategory": "SecurityApplication",
    "operatingSystem": "macOS",
    "offers": {
      "@type": "Offer",
      "price": "29",
      "priceCurrency": "USD",
      "validFrom": "2024-01-01"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    },
    "author": {
      "@type": "Organization",
      "name": "Netwoke",
      "url": siteUrl
    }
  }

  const finalSchema = schemaData || defaultSchema

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={siteDescription} />
      <meta name="keywords" content="network monitoring, mac security, privacy tools, network connections, macos firewall, network analysis, privacy protection" />
      <meta name="author" content="Netwoke" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:image" content={`${siteUrl}${image}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Netwoke" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={`${siteUrl}${image}`} />
      <meta name="twitter:creator" content="@netwoke" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Language and Region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="US" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalSchema)}
      </script>
      
      {/* Favicon */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/logo512.png" />
      
      {/* Preconnect to external domains */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
    </Helmet>
  )
}
